import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
import { API_BASE as BASE } from '../lib/apiBase';
const TOKEN_KEY = 'monteurcentraal_token';

const authApi = axios.create({ baseURL: `${BASE}/api`, timeout: 15000 });

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Attach token to all axios calls globally
  useEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      if (token && config.url && config.url.includes('/api/')) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, [token]);

  const refreshMe = useCallback(() => {
    if (!token) { setUser(null); setLoading(false); return Promise.resolve(); }
    return authApi.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setUser(r.data); setLoading(false); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); setUser(null); setLoading(false); });
  }, [token]);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const login = useCallback((email, password) =>
    authApi.post('/auth/login', { email, password }).then(r => {
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      setUser(r.data.user);
      return r.data;
    }), []);

  const register = useCallback((data) =>
    authApi.post('/auth/register', data).then(r => {
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      setUser(r.data.user);
      return r.data;
    }), []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const forgotPassword = useCallback((email) =>
    authApi.post('/auth/forgot-password', { email }).then(r => r.data), []);

  const resetPassword = useCallback((token, password) =>
    authApi.post('/auth/reset-password', { token, password }).then(r => {
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      setUser(r.data.user);
      return r.data;
    }), []);

  return (
    <AuthContext.Provider value={{ token, user, loading, login, register, logout, refreshMe, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
